/**
 * Appearance Page
 *
 * Where an artist chooses the colours of their link-in-bio page and their website, and sees each
 * choice on the real page as they make it.
 *
 *   Surfaces      Link-in-bio, and Website when they have one (a hand-built Easel site or a
 *                 Portfolios-powered site). One editor for both: colours are chosen by ROLE
 *                 (background, text, main, second, accent) and each surface stores them its own way.
 *   Contract      A designed website decides which roles an artist may change (its
 *                 /.well-known/arthaus-theme.json); the rest are shown as set by the designer.
 *   Recommended   their own colours (Creative Genome) and curated palettes
 *   Ask for ideas describe a feel or attach an image; three palettes come back (30 a day)
 *   Fine-tune     the roles this surface allows, one by one
 *   Live preview  the real page in a device frame, restyled in place by that site's preview
 *                 bridge — nothing is saved until Save
 *
 * Saved through PUT /easel/appearance to the surface being edited, and to the other one too when
 * "Also apply to my …" is ticked. "From your artwork" on the link-in-bio page saves genome mode, so
 * the page keeps following the genome as it is re-derived.
 */

import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  ExternalLink,
  Globe,
  Link2,
  Loader2,
  Lock,
  RotateCcw,
  Save,
  SlidersHorizontal,
  Sparkles,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { cn } from '@/lib/utils'
import { useAppearance, useSaveAppearance, useStyleRecommendations } from '../hooks'
import type { AppearanceSurface, PaletteRoles } from '../types'
import { PortfolioPreview } from '../components/PortfolioPreview'
import { PaletteAssistant } from '../components/appearance/PaletteAssistant'
import { PaletteCard } from '../components/appearance/PaletteCard'
import { PRESETS, ROLE_FIELDS, contrast, isHex, type RoleKey } from '../components/appearance/palettes'

/** What is on screen for one surface: the artist's own colours (genome mode), or colours they chose. */
interface Choice {
  source: 'artwork' | 'custom'
  colors: PaletteRoles
}

const FALLBACK = PRESETS[0].colors

function hostOf(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, '')
  } catch {
    return url
  }
}

function editableRoles(surface: AppearanceSurface): RoleKey[] {
  return ROLE_FIELDS.map((f) => f.key).filter((k) => Boolean(surface.contract.roles[k]))
}

/** Only the roles this surface follows matter when deciding whether two palettes differ. */
function sameOn(roles: RoleKey[], a: PaletteRoles, b: PaletteRoles): boolean {
  return roles.every((k) => a[k].toUpperCase() === b[k].toUpperCase())
}

function savedChoice(surface: AppearanceSurface, artwork: PaletteRoles | null): Choice {
  if (surface.kind === 'bio' && surface.mode !== 'custom') return { source: 'artwork', colors: artwork ?? FALLBACK }
  return { source: 'custom', colors: { ...FALLBACK, ...surface.colors } }
}

export function AppearancePage() {
  const navigate = useNavigate()
  const { data, isLoading } = useAppearance()
  const { data: recommendations } = useStyleRecommendations()
  const save = useSaveAppearance()

  const artwork = data?.artwork ?? null
  const surfaces = useMemo(() => data?.surfaces ?? [], [data])
  const [activeId, setActiveId] = useState<AppearanceSurface['id']>('bio')
  const active = surfaces.find((s) => s.id === activeId) ?? surfaces[0]
  const other = surfaces.find((s) => s.id !== active?.id) ?? null

  const saved = useMemo(
    () => Object.fromEntries(surfaces.map((s) => [s.id, savedChoice(s, artwork)])) as Record<string, Choice>,
    [surfaces, artwork],
  )
  const [choices, setChoices] = useState<Record<string, Choice>>({})
  const [drafts, setDrafts] = useState<Partial<Record<RoleKey, string>>>({})
  const [alsoApply, setAlsoApply] = useState(false)

  useEffect(() => {
    setChoices(saved)
    setDrafts({})
  }, [saved])

  useEffect(() => {
    setDrafts({})
    setAlsoApply(false)
  }, [activeId])

  if (isLoading) {
    return (
      <div className="space-y-6 p-8">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_440px]">
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-[640px] w-full" />
        </div>
      </div>
    )
  }

  if (!active) {
    return (
      <div className="space-y-6 p-8">
        <h1 className="text-3xl font-semibold tracking-tight">Appearance</h1>
        <Alert>
          <AlertDescription>
            Set up your link-in-bio page first, then come back to choose its colours.{' '}
            <Link to="/app/easel/profile" className="underline">
              Go to Link-in-Bio
            </Link>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const roles = editableRoles(active)
  const designed = roles.length === 0
  const choice = choices[active.id] ?? saved[active.id]
  const isDirty = (s: AppearanceSurface) => {
    const c = choices[s.id]
    const v = saved[s.id]
    if (!c || !v) return false
    if (c.source !== v.source) return true
    return c.source === 'custom' && !sameOn(editableRoles(s), c.colors, v.colors)
  }
  const dirty = isDirty(active)
  const otherDirty = other ? isDirty(other) : false
  const otherRoles = other ? editableRoles(other) : []
  const canAlsoApply = Boolean(other && otherRoles.length > 0)

  // The page shows genome colours in artwork mode; with no genome yet it is left as served. A
  // designed site with nothing editable is shown exactly as it is.
  const previewPalette = designed
    ? undefined
    : choice.source === 'artwork'
      ? artwork
      : choice.colors
  const textContrast = contrast(choice.colors.text, choice.colors.background)

  const setChoice = (next: Choice) => setChoices((c) => ({ ...c, [active.id]: next }))
  const tryPalette = (colors: PaletteRoles) => {
    // The whole palette is kept even on a designed site, which only follows some roles: "Also apply
    // to my link-in-bio" then carries the palette the artist actually picked. Changes are only ever
    // judged on the roles a surface follows (sameOn), so the rest never read as unsaved.
    setChoice({ source: 'custom', colors })
    setDrafts({})
  }
  const setRole = (key: RoleKey, value: string) => {
    setDrafts((d) => ({ ...d, [key]: value }))
    if (isHex(value)) setChoice({ source: 'custom', colors: { ...choice.colors, [key]: value.toUpperCase() } })
  }

  const handleSave = async () => {
    const targets: AppearanceSurface['id'][] = [active.id]
    if (alsoApply && other && canAlsoApply) targets.push(other.id)
    const colors = choice.source === 'artwork' && artwork ? artwork : choice.colors
    await save.mutateAsync({
      colors,
      targets,
      bioMode: active.kind === 'bio' && choice.source === 'artwork' ? 'genome' : 'custom',
    })
    setAlsoApply(false)
  }

  const isLive = (source: Choice['source'], colors: PaletteRoles) => {
    const v = saved[active.id]
    return v.source === source && (source === 'artwork' || sameOn(roles, v.colors, colors))
  }

  return (
    <div className="space-y-6 p-8">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/easel')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Appearance</h1>
            <p className="text-muted-foreground">Try colours on your pages, then save the ones you love</p>
          </div>
        </div>
        <Button asChild variant="outline">
          <a href={active.url} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="mr-2 h-4 w-4" />
            View {active.id === 'bio' ? 'page' : 'site'}
          </a>
        </Button>
      </div>

      {/* Surfaces */}
      {surfaces.length > 1 && (
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="What you are styling">
          {surfaces.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={s.id === active.id}
              onClick={() => setActiveId(s.id)}
              className={cn(
                'flex items-center gap-2 rounded-lg border px-4 py-2 text-left text-sm transition-colors',
                s.id === active.id ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-accent/50',
              )}
            >
              {s.id === 'bio' ? <Link2 className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
              <span className="font-medium">{s.id === 'bio' ? 'Link-in-bio' : 'Website'}</span>
              <span className="text-muted-foreground">{hostOf(s.url)}</span>
              {isDirty(s) && <span className="h-2 w-2 rounded-full bg-primary" aria-label="Unsaved changes" />}
            </button>
          ))}
        </div>
      )}

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_440px]">
        <div className="min-w-0 space-y-6">
          {designed ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Colours set by your designer
                </CardTitle>
                <CardDescription>
                  {active.contract.note || "Your website's colours are part of its custom design."} You can still
                  restyle your link-in-bio page.
                </CardDescription>
              </CardHeader>
              {active.contract.locked.length > 0 && (
                <CardContent>
                  <LockedSwatches locked={active.contract.locked} />
                </CardContent>
              )}
            </Card>
          ) : (
            <>
              {active.kind !== 'bio' && roles.length < ROLE_FIELDS.length && (
                <Alert>
                  <AlertDescription>
                    Your website is custom-designed. From a palette it takes your{' '}
                    {roles.map((r) => active.contract.roles[r]?.label.toLowerCase()).join(' and ')}; its brand colours
                    stay as your designer set them.
                  </AlertDescription>
                </Alert>
              )}

              {/* Recommended */}
              <Card>
                <CardHeader>
                  <CardTitle>Recommended</CardTitle>
                  <CardDescription>Tap a palette to see it on your {active.id === 'bio' ? 'page' : 'site'}.</CardDescription>
                </CardHeader>
                <CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {artwork ? (
                    <PaletteCard
                      name="From your artwork"
                      description={
                        active.kind === 'bio'
                          ? 'Measured from your uploads, and updates as your portfolio grows.'
                          : 'Measured from your uploads.'
                      }
                      colors={artwork}
                      badge={isLive(active.kind === 'bio' ? 'artwork' : 'custom', artwork) ? 'Live' : 'Yours'}
                      selected={
                        active.kind === 'bio'
                          ? choice.source === 'artwork'
                          : choice.source === 'custom' && sameOn(roles, choice.colors, artwork)
                      }
                      onSelect={() =>
                        active.kind === 'bio' ? setChoice({ source: 'artwork', colors: artwork }) : tryPalette(artwork)
                      }
                    />
                  ) : (
                    <Link
                      to="/app/easel"
                      className="flex flex-col justify-center rounded-lg border border-dashed p-3 text-sm text-muted-foreground hover:bg-accent/50"
                    >
                      <span className="font-medium text-foreground">Colours from your artwork</span>
                      Generate your Creative Genome from the dashboard to get a palette measured from your own work.
                    </Link>
                  )}
                  {PRESETS.map((preset) => (
                    <PaletteCard
                      key={preset.name}
                      name={preset.name}
                      description={preset.description}
                      colors={preset.colors}
                      badge={isLive('custom', preset.colors) ? 'Live' : undefined}
                      selected={choice.source === 'custom' && sameOn(roles, choice.colors, preset.colors)}
                      onSelect={() => tryPalette(preset.colors)}
                    />
                  ))}
                </CardContent>
              </Card>

              {/* Ask for ideas */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5" />
                    Ask for ideas
                  </CardTitle>
                  <CardDescription>
                    Describe the feel you are after, or attach an image (a favourite piece, your logo, a photo of
                    your studio) and we will suggest palettes to match.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <PaletteAssistant
                    current={choice.source === 'artwork' && artwork ? artwork : choice.colors}
                    selected={choice.source === 'custom' ? choice.colors : null}
                    onTry={(s) => tryPalette(s.colors)}
                    quota={recommendations?.suggestions ?? null}
                  />
                </CardContent>
              </Card>

              {/* Fine-tune */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <SlidersHorizontal className="h-5 w-5" />
                    Fine-tune
                  </CardTitle>
                  <CardDescription>Adjust any colour. Click a swatch to pick, or type a hex code.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {ROLE_FIELDS.map(({ key, label, help }) => {
                    const allowed = roles.includes(key)
                    const name = active.contract.roles[key]?.label || label
                    return (
                      <div key={key} className={cn('flex items-center gap-4', !allowed && 'opacity-50')}>
                        <input
                          type="color"
                          aria-label={name}
                          value={choice.colors[key]}
                          disabled={!allowed}
                          onChange={(e) => setRole(key, e.target.value.toUpperCase())}
                          className="h-10 w-14 cursor-pointer rounded border bg-transparent p-1 disabled:cursor-not-allowed"
                        />
                        <div className="flex-1">
                          <Label htmlFor={`color-${key}`}>{name}</Label>
                          <p className="text-xs text-muted-foreground">{allowed ? help : 'Set by your designer'}</p>
                        </div>
                        <Input
                          id={`color-${key}`}
                          className="w-28 font-mono uppercase"
                          value={drafts[key] ?? choice.colors[key]}
                          disabled={!allowed}
                          onChange={(e) => setRole(key, e.target.value.trim())}
                          onBlur={() => setDrafts((d) => ({ ...d, [key]: undefined }))}
                          maxLength={7}
                        />
                      </div>
                    )
                  })}
                  {roles.includes('text') && roles.includes('background') && textContrast < 4.5 && (
                    <Alert>
                      <AlertDescription>
                        Your text colour is hard to read on this background. Try a darker text colour or a lighter
                        background.
                      </AlertDescription>
                    </Alert>
                  )}
                  {active.contract.locked.length > 0 && (
                    <div className="space-y-2 border-t pt-4">
                      <p className="text-sm font-medium">Brand colours</p>
                      <p className="text-xs text-muted-foreground">{"Part of your site's design; your designer can change these."}</p>
                      <LockedSwatches locked={active.contract.locked} />
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Live preview + save */}
        <div className="space-y-3 lg:sticky lg:top-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Live preview</h2>
            <span className="text-xs text-muted-foreground">
              {dirty ? 'Previewing — not saved yet' : 'What visitors see now'}
            </span>
          </div>
          <Card className="overflow-hidden">
            <PortfolioPreview
              key={active.id}
              url={active.previewUrl}
              stylePreview={previewPalette}
              defaultDevice={active.id === 'bio' ? 'mobile' : 'desktop'}
            />
          </Card>

          {!designed && (
            <Card>
              <CardContent className="space-y-3 p-4">
                {canAlsoApply && other && (
                  <label htmlFor="also-apply" className="flex items-start gap-3 text-sm">
                    <Checkbox
                      id="also-apply"
                      checked={alsoApply}
                      onCheckedChange={(v) => setAlsoApply(v === true)}
                      className="mt-0.5"
                    />
                    <span>
                      Also apply to my {other.id === 'bio' ? 'link-in-bio' : 'website'}
                      {other.kind !== 'bio' && otherRoles.length < ROLE_FIELDS.length && (
                        <span className="block text-xs text-muted-foreground">
                          Your website takes the {otherRoles.map((r) => other.contract.roles[r]?.label.toLowerCase()).join(' and ')}.
                        </span>
                      )}
                    </span>
                  </label>
                )}
                {otherDirty && other && (
                  <p className="text-xs text-muted-foreground">
                    You also have unsaved changes on your {other.id === 'bio' ? 'link-in-bio' : 'website'}.
                  </p>
                )}
                <div className="flex gap-2">
                  {dirty && (
                    <Button variant="outline" className="flex-1" onClick={() => setChoice(saved[active.id])}>
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Discard
                    </Button>
                  )}
                  <Button
                    className="flex-1"
                    onClick={handleSave}
                    disabled={(!dirty && !alsoApply) || save.isPending}
                  >
                    {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    {alsoApply ? 'Save to both' : 'Save'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

function LockedSwatches({ locked }: { locked: { label: string; color: string }[] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {locked.map((l) => (
        <span key={l.label} className="flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs">
          <span className="h-3 w-3 rounded-full border" style={{ background: l.color }} />
          {l.label}
        </span>
      ))}
    </div>
  )
}
